<template>
  <router-view />

  <!-- Modal de sesión expirada -->
  <div v-if="showSessionExpiredModal" class="session-expired-overlay">
    <div class="session-expired-modal">
      <div class="modal-content">
        <h3>Sesión Expirada</h3>
        <p>Su sesión ha expirado por inactividad. <br>
          Será redirigido al login.</p>
        <div class="modal-actions">
          <button @click="handleModalClose" class="btn-logout">
            Ir al login
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axios from './plugins/axios'

const router = useRouter()
const route = useRoute()
const showSessionExpiredModal = ref(false)

// 3 segundos para testing
// const INACTIVITY_TIME = 3 * 1000;
// 10 minutos para producción
const INACTIVITY_TIME = 10 * 60 * 1000
let inactivityTimer = null

// Eventos que resetean el timer de inactividad
const activityEvents = [
  'mousedown',
  'mousemove',
  'keypress',
  'scroll',
  'touchstart',
  'click'
]

// Función para verificar si estamos en login
const isLoginRoute = () => {
  return route.path === '/login' || route.name === 'login'
}

const resetInactivityTimer = () => {
  // No hacer nada si estamos en login
  if (isLoginRoute()) {
    return
  }

  // Limpiar timer existente
  if (inactivityTimer) {
    clearTimeout(inactivityTimer)
  }

  // Crear nuevo timer
  inactivityTimer = setTimeout(() => {
    showSessionExpired()
  }, INACTIVITY_TIME)
}

const showSessionExpired = async () => {
  showSessionExpiredModal.value = true
  
  // Ejecutar logout automáticamente cuando aparece el modal
  await performLogout()
}

const performLogout = async () => {
  try {
    // Hacer POST a /api/logout
    await axios.post('/api/logout')
  } catch (error) {
    console.error('Error during logout:', error)
  } finally {
    // Limpiar almacenamiento local si existe
    localStorage.clear()
    sessionStorage.clear()
  }
}

const handleModalClose = () => {
  // Redirigir al login cuando el usuario hace clic en "Entendido"
  router.push('/login')
  
  // Ocultar modal
  showSessionExpiredModal.value = false
}

const addActivityListeners = () => {
  activityEvents.forEach(event => {
    document.addEventListener(event, resetInactivityTimer, true)
  })
}

const removeActivityListeners = () => {
  activityEvents.forEach(event => {
    document.removeEventListener(event, resetInactivityTimer, true)
  })
}

// Observar cambios de ruta
watch(() => route.path, () => {
  // Limpiar timer si vamos a login
  if (isLoginRoute()) {
    if (inactivityTimer) {
      clearTimeout(inactivityTimer)
      inactivityTimer = null
    }
  } else {
    // Reiniciar timer si estamos en otra ruta
    resetInactivityTimer()
  }
})

onMounted(() => {
  // Solo iniciar el timer si NO estamos en login
  if (!isLoginRoute()) {
    resetInactivityTimer()
  }
  
  // Agregar listeners de actividad
  addActivityListeners()
})

onUnmounted(() => {
  // Limpiar timer
  if (inactivityTimer) {
    clearTimeout(inactivityTimer)
  }
  
  // Remover listeners
  removeActivityListeners()
})
</script>

<style scoped>
.session-expired-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}

.session-expired-modal {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  max-width: 400px;
  width: 90%;
  animation: modalAppear 0.3s ease-out;
}

.modal-content {
  padding: 30px;
  text-align: center;
}

.modal-content h3 {
  color: #dc3545;
  margin-bottom: 15px;
  font-size: 24px;
  font-weight: 600;
}

.modal-content p {
  color: #666;
  margin-bottom: 25px;
  line-height: 1.5;
}

.modal-actions {
  display: flex;
  justify-content: center;
}

.btn-logout {
  background-color: #dc3545;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-logout:hover {
  background-color: #c82333;
}

@keyframes modalAppear {
  from {
    opacity: 0;
    transform: scale(0.8);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>